import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { CURRENCIES, currency as currencyMeta } from '../../lib/catalogs';
import { cn } from '../../lib/cn';

/**
 * Polished currency dropdown with symbol + ISO 4217 code + name +
 * search. Single source of truth: every place in the app that lets the
 * user pick a currency uses this component.
 */
export function CurrencySelect({
  value,
  onChange,
  label,
  className,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CURRENCIES;
    return CURRENCIES.filter((c) =>
      c.code.toLowerCase().includes(needle) ||
      c.symbol.toLowerCase().includes(needle) ||
      t(c.name).toLowerCase().includes(needle),
    );
  }, [q, t]);

  const current = currencyMeta(value);
  const triggerLabel = current
    ? `${current.symbol}  ${current.code}`
    : t('currencies.placeholder');

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{label}</div>
      )}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900',
              'flex items-center justify-between text-sm gap-2 num',
              'hover:border-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="truncate text-left">{triggerLabel}</span>
            <ChevronDown size={14} className="text-ink-400 shrink-0" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={6}
            align="start"
            className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[280px] rounded-xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 shadow-lg p-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
          >
            <div className="px-3 py-2 border-b border-ink-200 dark:border-ink-800 flex items-center gap-2">
              <Search size={14} className="text-ink-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('currencies.search')}
                autoFocus
                className="flex-1 bg-transparent border-0 outline-none text-sm"
              />
            </div>
            <div className="max-h-[320px] overflow-y-auto py-1">
              {filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => { onChange(c.code); setOpen(false); }}
                  className={cn(
                    'w-full text-left px-3 py-1.5 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-sm',
                    value === c.code && 'bg-accent-50/40 dark:bg-accent-900/20',
                  )}
                >
                  <span className="num text-ink-600 dark:text-ink-300 w-8 shrink-0">{c.symbol}</span>
                  <span className="font-mono text-xs w-10">{c.code}</span>
                  <span className="flex-1 truncate text-ink-700 dark:text-ink-200">{t(c.name)}</span>
                  {value === c.code && <Check size={14} className="text-accent-600" />}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-ink-500">{t('currencies.empty')}</div>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

/**
 * Multi-currency selector with a searchable popover over all 65
 * currencies + chip preview of what's been selected. Used in
 * onboarding for "which currencies do you transact in?".
 *
 * Combobox UX: chips for the picked set, a single "+ Add" pill that
 * opens the popover. No catalog truncation.
 */
export function CurrencyMultiSelect({
  value,
  onChange,
  label,
}: { value: string[]; onChange: (next: string[]) => void; label?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CURRENCIES;
    return CURRENCIES.filter((c) =>
      c.code.toLowerCase().includes(needle) ||
      c.symbol.toLowerCase().includes(needle) ||
      t(c.name).toLowerCase().includes(needle),
    );
  }, [q, t]);

  function toggle(code: string) {
    onChange(value.includes(code) ? value.filter((v) => v !== code) : [...value, code]);
  }
  function remove(code: string) {
    onChange(value.filter((v) => v !== code));
  }

  const pickedMetas = value
    .map((code) => CURRENCIES.find((c) => c.code === code))
    .filter((c): c is typeof CURRENCIES[number] => !!c);

  return (
    <div>
      {label && <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">{label}</div>}
      <div className="flex flex-wrap gap-2">
        {pickedMetas.map((c) => (
          <button
            type="button"
            key={c.code}
            onClick={() => remove(c.code)}
            aria-label={t('common.delete') + ' ' + c.code}
            className="px-3 py-1.5 rounded-full text-sm border bg-accent-600 text-white border-accent-600 flex items-center gap-1.5 hover:bg-accent-700"
            title={t(c.name)}
          >
            <span className="num">{c.symbol}</span>
            <span className="font-mono text-xs">{c.code}</span>
            <X size={12} className="opacity-80" />
          </button>
        ))}

        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="px-3 py-1.5 rounded-full text-sm border border-dashed border-ink-300 dark:border-ink-700 text-ink-500 hover:border-ink-500 hover:text-ink-900 dark:hover:text-ink-100 flex items-center gap-1.5"
            >
              <Plus size={12} />
              {t('currencies.addAnother')}
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              sideOffset={6}
              align="start"
              className="z-50 w-[320px] rounded-xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 shadow-lg p-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
            >
              <div className="px-3 py-2 border-b border-ink-200 dark:border-ink-800 flex items-center gap-2">
                <Search size={14} className="text-ink-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('currencies.search')}
                  autoFocus
                  className="flex-1 bg-transparent border-0 outline-none text-sm"
                />
              </div>
              <div className="max-h-[320px] overflow-y-auto py-1">
                {filtered.map((c) => {
                  const active = value.includes(c.code);
                  return (
                    <button
                      type="button"
                      key={c.code}
                      onClick={() => toggle(c.code)}
                      aria-pressed={active}
                      className="w-full text-left px-3 py-1.5 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-sm"
                    >
                      <span className="num text-ink-600 dark:text-ink-300 w-8 shrink-0">{c.symbol}</span>
                      <span className="font-mono text-xs w-10">{c.code}</span>
                      <span className="flex-1 truncate text-ink-700 dark:text-ink-200">{t(c.name)}</span>
                      {active && <Check size={14} className="text-accent-600" />}
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="px-3 py-6 text-center text-xs text-ink-500">{t('currencies.empty')}</div>
                )}
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    </div>
  );
}
